// System
import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { Box, Chip, Typography } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { RubberDuckRoundDto } from '@Modal/reducers/rounds/types';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { useRubberDuckRoundsTable } from '@Modal/hooks/rounds/useRubberDuckRoundsTable';
import { EnumTableColumnHeader, StringTableColumnHeader } from '@Components/shared/tables/columns';
import { NumberRangeTableColumnHeader } from '@Components/shared/tables/columns/NumberRangeTableColumnHeader';
import { DateTableColumnHeader } from '@Components/shared/tables/columns/DateTableColumnHeader/DateTableColumnHeader';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

// Contracts
import { Filterable } from '@contracts/common/traits/Filterable';
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';

// Utils
import {
  RubberDuckRoundListColumns,
  RubberDuckRoundListFilter,
  rubberDuckRoundListColumns,
} from '@utils/tables/rounds/rubberDuckRoundListFilter';
import { useDispatch } from 'react-redux';

type HeaderOptions = {
  filter: Filterable<RubberDuckRoundListFilter>;
};

type Columns = TableColumnDescriptionArray<
  RubberDuckRoundDto,
  HeaderOptions,
  typeof rubberDuckRoundListColumns
>;

type Column = TableColumnDescription<RubberDuckRoundDto, HeaderOptions, RubberDuckRoundListColumns>;

export const rubberDuckDifficultyOptions = [
  { key: 'EASY', value: 'EASY' },
  { key: 'MEDIUM', value: 'MEDIUM' },
  { key: 'HARD', value: 'HARD' },
  { key: 'EXTREME', value: 'EXTREME' },
];

export const rubberDuckStatusOptions = [
  { key: 'ONGOING', value: 'ONGOING' },
  { key: 'WON', value: 'WON' },
  { key: 'LOST', value: 'LOST' },
  { key: 'CASHED_OUT', value: 'CASHED_OUT' },
  { key: 'ABANDONED', value: 'ABANDONED' },
];

const statusColor = (
  status?: string
): 'default' | 'success' | 'error' | 'info' | 'warning' | 'primary' => {
  switch (status) {
    case 'WON':
      return 'success';
    case 'CASHED_OUT':
      return 'primary';
    case 'LOST':
      return 'error';
    case 'ONGOING':
      return 'info';
    case 'ABANDONED':
      return 'warning';
    default:
      return 'default';
  }
};

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useRubberDuckRoundsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const dispatch = useDispatch();
  const { handleFilterSelect, setRoundTableFilters } = useRubberDuckRoundsTable();

  const applyRange = (minCodeName: string, maxCodeName: string) => ({
    onApply: (startValue?: number | null, endValue?: number | null) => {
      startValue !== undefined
        ? dispatch(setRoundTableFilters({ codeName: minCodeName, value: startValue }))
        : null;
      endValue !== undefined
        ? dispatch(setRoundTableFilters({ codeName: maxCodeName, value: endValue }))
        : null;
    },
    onReset: () => {
      dispatch(setRoundTableFilters({ codeName: minCodeName, value: undefined }));
      dispatch(setRoundTableFilters({ codeName: maxCodeName, value: undefined }));
    },
  });

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'rubberDuckRound._id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'id'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>{row?._id && <TruncateText text={row?._id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'rubberDuckRound.roundId',
          title: 'Round ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'roundId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.roundId && (
                <TruncateText text={row?.roundId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'rubberDuckRound.playerId',
          title: 'Player ID',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'playerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>{row?.playerId && <Typography variant="body2">{row?.playerId}</Typography>}</>
          ),
        },
        {
          name: 'rubberDuckRound.difficulty',
          title: 'Difficulty',
          sx: { minWidth: '120px', maxWidth: '160px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName={'difficulty'}
              options={rubberDuckDifficultyOptions}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.difficulty && <Typography variant="body2">{row?.difficulty}</Typography>}
            </Box>
          ),
        },
        {
          name: 'rubberDuckRound.betAmount',
          title: 'Bet Amount',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => (
            <NumberRangeTableColumnHeader
              title={title}
              {...applyRange('minBetAmount', 'maxBetAmount')}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              <Typography variant="body2">
                {row?.betAmount} {row?.currency}
              </Typography>
            </Box>
          ),
        },
        {
          name: 'rubberDuckRound.reachedLane',
          title: 'Reached Lane',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => (
            <NumberRangeTableColumnHeader
              title={title}
              {...applyRange('minReachedLane', 'maxReachedLane')}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              <Typography variant="body2">
                {row?.reachedLane ?? 0} / {row?.laneCount}
              </Typography>
            </Box>
          ),
        },
        {
          name: 'rubberDuckRound.currentMultiplier',
          title: 'Multiplier',
          sx: { minWidth: '130px', maxWidth: '170px' },
          header: ({ title }) => (
            <NumberRangeTableColumnHeader
              title={title}
              {...applyRange('minCurrentMultiplier', 'maxCurrentMultiplier')}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              <Typography variant="body2">{`${row?.currentMultiplier ?? 0}x`}</Typography>
            </Box>
          ),
        },
        {
          name: 'rubberDuckRound.totalWinAmount',
          title: 'Total Win',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => (
            <NumberRangeTableColumnHeader
              title={title}
              {...applyRange('minTotalWinAmount', 'maxTotalWinAmount')}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              <Typography variant="body2">
                {row?.totalWinAmount} {row?.currency}
              </Typography>
            </Box>
          ),
        },
        {
          name: 'rubberDuckRound.status',
          title: 'Status',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName={'status'}
              options={rubberDuckStatusOptions}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.status && (
                <Chip size="small" label={row?.status} color={statusColor(row?.status)} />
              )}
            </Box>
          ),
        },
        {
          name: 'rubberDuckRound.createdAt',
          title: 'Created At',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <DateTableColumnHeader
              title={title}
              onApply={(startDate, endDate) => {
                startDate?.length
                  ? dispatch(
                      setRoundTableFilters({
                        codeName: 'createdStartDate',
                        value: startDate,
                      })
                    )
                  : null;
                endDate?.length
                  ? dispatch(
                      setRoundTableFilters({
                        codeName: 'createdEndDate',
                        value: endDate,
                      })
                    )
                  : null;
              }}
              onReset={() => {
                dispatch(
                  setRoundTableFilters({
                    codeName: 'createdStartDate',
                    value: undefined,
                  })
                );
                dispatch(
                  setRoundTableFilters({
                    codeName: 'createdEndDate',
                    value: undefined,
                  })
                );
              }}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
